import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';

const Sidebar = ({ activeChat, setActiveChat, conversations, searchResults, onSearchChange, onlineStatuses }) => {
  const { theme } = useContext(ThemeContext);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      onSearchChange(searchTerm);
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  return (
    <aside className={`w-full h-full flex flex-col border-r ${theme === 'dark' ? 'bg-[#121824] border-gray-800' : 'bg-white border-gray-200'}`}>
      <div className="p-4 border-b border-inherit">
        <div className="relative">
          <input
            type="text"
            placeholder="Search username or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 rounded-xl text-sm outline-none border transition-all ${
              theme === 'dark' 
                ? 'bg-[#1A2333] border-gray-700 text-white focus:border-accentBlue' 
                : 'bg-gray-100 border-gray-200 text-gray-900 focus:border-accentBlue'
            }`}
          />
          <svg className="w-4 h-4 absolute left-3.5 top-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {searchTerm.length > 0 ? (
          <div>
            <p className="text-xs font-semibold px-3 py-1 text-gray-400 uppercase tracking-wider">Search Results</p>
            {searchResults.length === 0 ? (
              <p className="text-sm px-3 py-2 text-gray-500 italic">No directory accounts found</p>
            ) : (
              searchResults.map(user => (
                <button
                  key={user._id}
                  onClick={() => { setActiveChat({ user }); setSearchTerm(''); }}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${theme === 'dark' ? 'hover:bg-[#1A2333]' : 'hover:bg-gray-100'}`}
                >
                  <div className="relative w-10 h-10 rounded-full flex items-center justify-center font-bold text-white uppercase text-sm flex-shrink-0" style={{ backgroundColor: user.avatar.startsWith('#') ? user.avatar : '#3B82F6' }}>
                    {user.username.charAt(0)}
                    <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${theme === 'dark' ? 'border-[#121824]' : 'border-white'} ${onlineStatuses[user._id] === 'online' || user.status === 'online' ? 'bg-green-500' : 'bg-gray-400'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-sm truncate">{user.username}</h4>
                    <p className="text-xs text-gray-400 truncate">{user.email}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        ) : (
          <div>
            <p className="text-xs font-semibold px-3 py-1 text-gray-400 uppercase tracking-wider">Recent Conversations</p>
            {conversations.length === 0 ? (
              <div className="text-center p-6 text-gray-400 text-sm italic">No interactions active. Search a user to connect.</div>
            ) : (
              conversations.map(conv => {
                const otherParticipant = conv.participants[0];
                if (!otherParticipant) return null;
                const isSelected = activeChat?.conversationId === conv._id;
                const isOnline = onlineStatuses[otherParticipant._id] === 'online' || otherParticipant.status === 'online';

                return (
                  <button
                    key={conv._id}
                    onClick={() => setActiveChat({ conversationId: conv._id, user: otherParticipant })}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                      isSelected 
                        ? (theme === 'dark' ? 'bg-[#1E293B] text-white shadow-md' : 'bg-blue-50 text-blue-900 shadow-sm') 
                        : (theme === 'dark' ? 'hover:bg-[#1A2333]' : 'hover:bg-gray-100')
                    }`}
                  >
                    <div className="relative w-10 h-10 rounded-full flex items-center justify-center font-bold text-white uppercase text-sm flex-shrink-0" style={{ backgroundColor: otherParticipant.avatar.startsWith('#') ? otherParticipant.avatar : '#3B82F6' }}>
                      {otherParticipant.username.charAt(0)}
                      <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${isSelected ? (theme === 'dark' ? 'border-[#1E293B]' : 'border-blue-50') : (theme === 'dark' ? 'border-[#121824]' : 'border-white')} ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-sm truncate">{otherParticipant.username}</h4>
                      </div>
                      <p className="text-xs text-gray-400 truncate">{conv.lastMessage || 'Open thread connection setup completed.'}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;